// 2026-05-26 - FormuLearning - 상단 헤더
// 2026-09-21 수정 - 이슈 #77(G6): 주성분명(api_name) → Description(memo) 한 줄 말줄임 + 전문 툴팁
// 2026-09-23 수정 - 이슈 #82: 진행 중 작업 표시·Break(`BusyIndicator`) · 진행 중 Autosave 토글 잠금(ON 전환이 곧 저장이다).
//   로그아웃·Admin 이동은 잠그지 않는다 — 창 닫기와 같은 이탈이며, 소유 카드의 언마운트 정리가 작업을 끊는다(K5 결정).
// 2026-11-03 수정 - 이슈 #114: viewer 는 Autosave 토글 비활성(OFF 표시) + «조회 전용 계정» 표지.
// 파일 위치: frontend/src/components/layout/Header.tsx

import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { useProjectStore } from '../../stores/projectStore'
import { useUiStore } from '../../stores/uiStore'
import { useBusyStore, blockIfBusy } from '../../stores/busyStore'
import BusyIndicator from '../feedback/BusyIndicator'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

const SYNC_COLOR: Record<string, string> = {
  idle: '',
  saving: 'var(--color-accent-purple)',
  synced: 'var(--color-accent-teal)',
}

export default function Header() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const project = useProjectStore((s) => s.currentProject)
  const syncStatus = useUiStore((s) => s.syncStatus)
  const canWrite = useCanWrite()
  const theme = useUiStore((s) => s.theme)
  const toggleTheme = useUiStore((s) => s.toggleTheme)
  const autosave = useUiStore((s) => s.autosave)
  const setAutosave = useUiStore((s) => s.setAutosave)
  const busy = useBusyStore((s) => s.task !== null)
  // 2026-07-09 - 이슈 #33 4단계: 저장 라벨은 Autosave 모드에 따라(자동/수동) 표기.
  const syncText =
    syncStatus === 'saving' ? (autosave ? 'Auto saving...' : 'Saving...') : syncStatus === 'synced' ? '● Synced' : ''
  const syncColor = SYNC_COLOR[syncStatus]

  // 2026-06-19 - mixed 모드: 헤더 영역만 다크로 강제 (본문은 라이트). 이슈 #9
  return (
    <header
      data-theme={theme === 'mixed' ? 'dark' : undefined}
      className="flex h-14 items-center justify-between border-b px-5"
      style={{ backgroundColor: 'var(--color-bg-panel)', borderColor: 'var(--border-divider)' }}
    >
      <div className="flex items-center gap-3">
        {project ? (
          <>
            <span className="text-sm font-semibold text-text-primary">{project.name}</span>
            {project.memo && (
              <span className="max-w-[360px] truncate text-xs text-text-secondary" title={project.memo}>
                · {project.memo.split('\n')[0]}
              </span>
            )}
            <span className="rounded-full px-2 py-0.5 text-[10px] font-medium text-accent-teal" style={{ background: 'var(--bg-chip-teal)' }}>
              Step {project.current_step}
            </span>
            {syncText && (
              <span className="text-[10px] font-medium" style={{ color: syncColor }}>
                {syncText}
              </span>
            )}
          </>
        ) : (
          <span className="text-sm text-text-secondary">Select a project to begin</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <BusyIndicator />
        {/* 2026-07-09 - Autosave 토글 (이슈 #33 4단계). OFF 시 편집을 Save and continue까지 보류 */}
        <label
          className={`flex cursor-pointer items-center gap-1.5 text-xs text-text-secondary select-none${busy || !canWrite ? ' opacity-50' : ''}`}
          title={canWrite ? 'ON: 편집 즉시 자동저장 / OFF: Save and continue 시 일괄 저장' : READ_ONLY_TITLE}
        >
          <input
            type="checkbox"
            checked={canWrite && autosave}
            disabled={!canWrite}
            onChange={(e) => blockIfBusy() || setAutosave(e.target.checked)}
            className="h-3.5 w-3.5 accent-[var(--color-accent-purple)]"
          />
          Autosave
        </label>
        {/* 2026-06-19 - 테마 순환 토글 dark→light→mixed (이슈 #9) */}
        <button
          onClick={toggleTheme}
          title={`Theme: ${theme} — click to cycle (dark → light → mixed)`}
          aria-label="Cycle theme"
          className="rounded px-2 py-1 text-sm text-text-secondary transition-colors hover:bg-bg-input hover:text-text-body"
        >
          {theme === 'dark' ? '🌙' : theme === 'light' ? '☀️' : '🌗'}
        </button>
        {user?.role === 'admin' && (
          <button
            onClick={() => navigate('/admin')}
            title="External API logging admin"
            className="rounded px-2 py-1 text-xs text-text-secondary transition-colors hover:bg-bg-input hover:text-text-body"
          >
            Admin
          </button>
        )}
        {!canWrite && (
          // viewer(#114) — 왜 컨트롤이 비활성인지 한 곳에서 알린다.
          <span
            className="rounded px-1.5 py-0.5 text-[10px] font-medium"
            style={{ background: 'var(--bg-input)', color: 'var(--color-text-secondary)' }}
            title="보기만 할 수 있습니다 — 저장·생성·삭제·검색·예측·분석 실행은 비활성입니다"
          >
            {READ_ONLY_TITLE}
          </span>
        )}
        <span className="text-xs text-text-secondary">{user?.name}</span>
        <button
          onClick={logout}
          className="rounded px-2 py-1 text-xs text-text-secondary transition-colors hover:bg-bg-input hover:text-text-body"
        >
          Sign Out
        </button>
      </div>
    </header>
  )
}
